/**
 * Standardized error handling utility functions
 * This utility provides consistent error handling across the application
 */

/**
 * Parse API error responses into user-friendly messages
 * @param {Object} errorResponse - The error response from the API
 * @returns {string} A user-friendly error message
 */
export const parseApiErrorMessage = (errorResponse) => {
  try {
    // First check for the standard FastAPI detail structure we use
    if (errorResponse?.data?.detail && typeof errorResponse.data.detail === 'object') {
      const detail = errorResponse.data.detail;
      if (detail.message) {
        return detail.message;
      }
    }

    // Check if the error response has a data property with a message
    if (errorResponse?.data?.message) {
      return errorResponse.data.message;
    }
    
    // Check for detail message as string
    if (errorResponse?.data?.detail && typeof errorResponse.data.detail === 'string') {
      return errorResponse.data.detail;
    }

    // If response contains a stringified JSON object, try to parse it
    if (typeof errorResponse?.data === 'string') {
      try {
        const parsed = JSON.parse(errorResponse.data);
        if (parsed.message) return parsed.message;
        if (parsed.detail && parsed.detail.message) return parsed.detail.message;
      } catch (e) {
        // Not a JSON string, continue to next check
      }
    }
    
    // If no structured message found, but we have status code, return based on that
    if (errorResponse?.status) {
      switch(errorResponse.status) {
        case 401:
          return 'Authentication required. Please log in.';
        case 403:
          return 'You don\'t have permission to access this resource.';
        case 404:
          return 'The requested resource was not found.';
        case 429:
          return 'You\'ve reached the rate limit. Please wait a moment before trying again.';
        case 500:
        case 502:
        case 503:
        case 504:
          return 'Server error. Please try again later.';
        default:
          return 'An error occurred. Please try again.';
      }
    }
    
    // If no structured message found, return a generic error
    return 'An error occurred. Please try again.';
  } catch (e) {
    console.error('Error parsing API error message:', e);
    return 'An unexpected error occurred. Please try again.';
  }
};

/**
 * Check if the error is a rate limit error
 * @param {Error} error - The error object
 * @returns {boolean} True if it's a rate limit error
 */
export const isRateLimitError = (error) => {
  if (error.isRateLimit) return true;
  
  if (error.response?.status === 429) return true;
  
  if (error.response?.data?.error === "Rate limit exceeded: 50 per 1 minute") return true;
  
  if (typeof error.response?.data?.message === 'string' && 
      error.response?.data?.message.toLowerCase().includes('rate limit')) {
    return true;
  }
  
  return false;
};

/**
 * Handles API errors and returns a standardized error object
 * @param {Error} error - The error object from try/catch
 * @returns {Object} Standardized error object with message and type
 */
export const handleApiError = (error) => {
  // Default error info
  const errorInfo = {
    message: 'An error occurred. Please try again.',
    type: 'general',
    original: error
  };

  // Check for rate limit errors first
  if (isRateLimitError(error)) {
    errorInfo.message = "You've reached the rate limit. Please wait a moment before trying again.";
    errorInfo.type = 'rate_limit';
    return errorInfo;
  }

  // Handle errors with API response
  if (error.response) {
    errorInfo.message = parseApiErrorMessage(error.response);
    errorInfo.type = `api_${error.response.status || 'unknown'}`;
  } else if (error.request) {
    // Request was made but no response received (network error)
    errorInfo.message = 'Network error. Please check your internet connection.';
    errorInfo.type = 'network';
  }

  return errorInfo;
}; 